import { ColumnMappingData } from '../pages/ExcelUploadPage';

/** Abbreviations for all known base field IDs (without _legN suffix) */
const FIELD_ID_ABBR: Record<string, string> = {
  // Shipment fields
  shipment_date: 'SD',
  shipment_reference: 'SR',
  customer_reference: 'SC',
  weight_kg: 'Wt',
  // Leg fields
  origin_free_address: 'OFA',
  destination_free_address: 'DFA',
  transport_mode: 'TM',
  distance_km: 'Dist',
  vehicle_type: 'VT',
  truck_type_ferry_rail: 'TFR',
  load_factor: 'LF',
  origin_country: 'OC',
  destination_country: 'DC',
  origin_city: 'OCi',
  destination_city: 'DCi',
  origin_postal_code: 'OPC',
  destination_postal_code: 'DPC',
};

/** Exported legend used by the tooltip in SchemaManager */
export const ABBR_LEGEND: { abbr: string; label: string }[] = [
  { abbr: 'SD', label: 'Shipment Date' },
  { abbr: 'SR', label: 'Shipment Reference' },
  { abbr: 'SC', label: 'Sub-Customer' },
  { abbr: 'Wt', label: 'Weight (kg)' },
  { abbr: 'OFA', label: 'Free Address (Origin)' },
  { abbr: 'DFA', label: 'Free Address (Destination)' },
  { abbr: 'TM', label: 'Transport Mode' },
  { abbr: 'Dist', label: 'Distance (km)' },
  { abbr: 'VT', label: 'Vehicle Type' },
  { abbr: 'TFR', label: 'Truck Type (Ferry & Rail)' },
  { abbr: 'LF', label: 'Load Factor (%)' },
  { abbr: 'OC', label: 'Country (Origin)' },
  { abbr: 'DC', label: 'Country (Destination)' },
  { abbr: 'OCi', label: 'City (Origin)' },
  { abbr: 'DCi', label: 'City (Destination)' },
  { abbr: 'OPC', label: 'Postal Code (Origin)' },
  { abbr: 'DPC', label: 'Postal Code (Destination)' },
];

/**
 * Generates a compact schema name using field abbreviations, Excel column positions,
 * and a leg-grouping bracket syntax.
 *
 * Format:
 *   CompanyCode · Shipment-level fields · Leg groups
 *
 * If more than 2 legs, only Leg 1 is shown with a "+N legs" suffix.
 *
 * Examples:
 *   "42 · Wt:2 · L1(OC:3, DC:4, TM:5)"                    (1 leg, company ID 42)
 *   "42 · Wt:2 · L1(OC:3, DC:4) · L2(OC:6, DC:7)"         (2 legs)
 *   "42 · Wt:2 · L1(OC:3, DC:4, TM:5) +3 legs"            (4 legs, only L1 shown)
 *
 * @param mappings    - { columnIndex: fieldId } pairs from InlineColumnMapper
 * @param headers     - ordered list of column headers from the uploaded file
 * @param companyCode - licensee company ID to prefix the name
 */
type Part = { colPos: number; token: string };

/** 1-based Excel column position; 0 for non-numeric keys */
function getColPos(colKey: string): number {
  const idx = Number.parseInt(colKey, 10);
  return Number.isNaN(idx) ? 0 : idx + 1;
}

/** Parse a fieldId into its base abbr + optional leg number; null if unknown base */
function parseFieldId(fieldId: string): { abbr: string; legNum: number | null } | null {
  const legMatch = /^(.+)_leg(\d+)$/.exec(fieldId);
  const base = legMatch ? legMatch[1] : fieldId;
  const abbr = FIELD_ID_ABBR[base];
  if (!abbr) return null;
  return { abbr, legNum: legMatch ? Number.parseInt(legMatch[2], 10) : null };
}

type Buckets = { shipmentParts: Part[]; legGroups: Map<number, Part[]> };

/** Add one resolved fieldId to the right bucket */
function addFieldToBuckets(buckets: Buckets, fieldId: string, colPos: number): void {
  const parsed = parseFieldId(fieldId);
  if (!parsed) return;

  const token = colPos > 0 ? `${parsed.abbr}:${colPos}` : parsed.abbr;
  if (parsed.legNum === null) {
    buckets.shipmentParts.push({ colPos, token });
  } else {
    if (!buckets.legGroups.has(parsed.legNum)) buckets.legGroups.set(parsed.legNum, []);
    buckets.legGroups.get(parsed.legNum)!.push({ colPos, token });
  }
}

/** Bucket entries into shipment-level parts and per-leg groups */
function bucketEntries(entries: [string, string][]): Buckets {
  const buckets: Buckets = { shipmentParts: [], legGroups: new Map() };

  for (const [colHeader, rawFieldId] of entries) {
    if (colHeader.startsWith('MANUAL:')) continue;
    const colPos = getColPos(colHeader);
    for (const fieldId of rawFieldId.split('||').filter(Boolean)) {
      addFieldToBuckets(buckets, fieldId, colPos);
    }
  }
  return buckets;
}

/** Sort parts by column position and return their tokens */
function sortedTokens(parts: Part[]): string[] {
  return [...parts].sort((a, b) => a.colPos - b.colPos).map(({ token }) => token);
}

/** Render leg groups: collapse to L1 + count when >2 legs, else show all */
function buildLegParts(legGroups: Map<number, Part[]>): string[] {
  const sortedLegNums = Array.from(legGroups.keys()).sort((a, b) => a - b);
  const totalLegs = sortedLegNums.length;

  if (totalLegs > 2) {
    const leg1 = sortedTokens(legGroups.get(sortedLegNums[0])!);
    return [`L1(${leg1.join(', ')}) +${totalLegs - 1} legs`];
  }
  return sortedLegNums.map(
    legNum => `L${legNum}(${sortedTokens(legGroups.get(legNum)!).join(', ')})`,
  );
}

export function generateSchemaName(
  mappings: ColumnMappingData,
  headers?: string[],
  companyCode?: number | string,
): string {
  if (!mappings || Object.keys(mappings).length === 0) return 'New Schema';

  const entries = Object.entries(mappings).filter(([, v]) => v);
  if (entries.length === 0) return 'New Schema';

  const { shipmentParts, legGroups } = bucketEntries(entries);

  const parts: string[] = [];
  if (companyCode != null) parts.push(String(companyCode));
  parts.push(...sortedTokens(shipmentParts), ...buildLegParts(legGroups));

  if (parts.length === 0) return 'New Schema';

  const name = parts.join(' · ');
  return name.length > 72 ? name.slice(0, 69) + '…' : name;
}
